'use client';

import { useLocale } from 'next-intl';
import { Globe } from 'lucide-react';
import {
  sortLocalesForUser,
  getLocaleByCode,
  isRtlLocale,
} from '@/shared/constants/locales';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { cn } from '@/shared/lib/cn';
import { setLocaleCookie } from '@/shared/lib/locale-cookie';
import { useAuthStore } from '@/features/auth';
import { useUpdateLocale } from '../hooks/useUpdateLocale';

interface LanguageSelectorProps {
  className?: string;
  triggerClassName?: string;
}

/**
 * Language selector component for settings and auth screens.
 * For unauthenticated users (e.g. on the login screen) API sync is skipped —
 * the choice is persisted only in the NEXT_LOCALE cookie and then reconciled
 * with the API after a successful login.
 */
export function LanguageSelector({
  className,
  triggerClassName,
}: LanguageSelectorProps) {
  const currentLocale = useLocale();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { updateLocale, isUpdating } = useUpdateLocale();

  const handleLocaleChange = async (newLocale: string) => {
    // Update dir attribute immediately for RTL support (before reload)
    const htmlElement = document.documentElement;
    const newDir = isRtlLocale(newLocale) ? 'rtl' : 'ltr';
    htmlElement.setAttribute('dir', newDir);

    // Guests can't call PUT /v1/user/locale (requires auth) — cookie is enough.
    // Post-login sync will push the cookie value into the user's profile.
    if (isAuthenticated) {
      const success = await updateLocale(newLocale);
      if (!success) {
        return;
      }
    }

    // Save to cookie (with proper domain for subdomain sharing)
    setLocaleCookie(newLocale);

    // An explicit pick IS the decision — silence the suggestion banner for good
    localStorage.setItem('language-banner-dismissed', 'true');

    // Reload page to apply new locale
    window.location.reload();
  };

  const sortedLocales = sortLocalesForUser(currentLocale);
  const currentLocaleData = getLocaleByCode(currentLocale);

  return (
    <div className={className}>
      <Select
        value={currentLocale}
        onValueChange={handleLocaleChange}
        disabled={isUpdating}
      >
        <SelectTrigger className={cn('w-full', triggerClassName)}>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-current" />
            <SelectValue>
              {currentLocaleData?.nativeName || currentLocale.toUpperCase()}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {sortedLocales.map((locale) => (
            <SelectItem key={locale.code} value={locale.code}>
              <div className="flex items-center gap-2">
                <span>{locale.nativeName}</span>
                {locale.code !== locale.name.toLowerCase().slice(0, 2) && (
                  <span className="text-list-item-meta text-muted-foreground">
                    ({locale.name})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
